"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import type { User } from "@supabase/supabase-js";
import { getSupabaseBrowserClient } from "@/lib/supabase/client";
import { getProfile } from "@/lib/api/auth";
import type { Profile } from "@/lib/supabase/types";

interface AuthState {
  user: User | null;
  profile: Profile | null;
  loading: boolean;
  isAdmin: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (
    email: string,
    password: string,
    username: string,
  ) => Promise<{ needsConfirmation: boolean }>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);

  const sb = getSupabaseBrowserClient();

  const fetchProfile = useCallback(
    async (userId: string) => {
      try {
        const p = await getProfile(userId);
        setProfile((prev) =>
          prev &&
          prev.id === p.id &&
          prev.role === p.role &&
          prev.subscription_plan_id === p.subscription_plan_id
            ? prev
            : p,
        );
      } catch {
        setProfile(null);
      }
    },
    [],
  );

  useEffect(() => {
    sb.auth.getSession().then(({ data: { session } }) => {
      const u = session?.user ?? null;
      setUser(u);
      if (u) {
        fetchProfile(u.id).finally(() => setLoading(false));
      } else {
        setLoading(false);
      }
    });

    const {
      data: { subscription },
    } = sb.auth.onAuthStateChange((event, session) => {
      const u = session?.user ?? null;
      setUser((prev) => {
        if (prev?.id === u?.id) return prev;
        return u;
      });
      if (u) {
        if (event === "TOKEN_REFRESHED") return;
        fetchProfile(u.id);
      } else {
        setProfile(null);
      }
    });

    return () => subscription.unsubscribe();
  }, [sb, fetchProfile]);

  const signIn = useCallback(
    async (email: string, password: string) => {
      const { error } = await sb.auth.signInWithPassword({ email, password });
      if (error) throw error;
    },
    [sb],
  );

  const signUp = useCallback(
    async (
      email: string,
      password: string,
      username: string,
    ): Promise<{ needsConfirmation: boolean }> => {
      const { data, error } = await sb.auth.signUp({
        email,
        password,
        options: {
          data: { username },
          emailRedirectTo: `${window.location.origin}/ranges`,
        },
      });
      if (error) throw error;
      return { needsConfirmation: !data.session };
    },
    [sb],
  );

  const resetPassword = useCallback(async (email: string) => {
    const res = await fetch("/api/auth/reset-password", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        email,
        redirectTo: `${window.location.origin}/reset-password`,
      }),
    });

    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.detail ?? "Failed to send reset email");
    }
  }, []);

  const signOut = useCallback(async () => {
    const { error } = await sb.auth.signOut();
    if (error) throw error;
    setUser(null);
    setProfile(null);
    window.location.href = "/";
  }, [sb]);

  const value = useMemo<AuthState>(
    () => ({
      user,
      profile,
      loading,
      isAdmin: profile?.role === "admin",
      signIn,
      signUp,
      signOut,
      resetPassword,
    }),
    [user, profile, loading, signIn, signUp, signOut, resetPassword],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within <AuthProvider>");
  return ctx;
}
